import { enrichPlatformsWithEvidence, fitWeights } from "./evidence.js";

// Platform profile metadata. Numeric scores are intentionally not stored here;
// published score fields are derived from the claim-level evidence register.

export const siteStats = {
  updated: "September 2026",
  platformsEvaluated: 10,
  scoringPoints: 55,
  majorCategories: 11,
};

export const categoryLabels = {
  ai: "AI",
  design: "Design",
  catalog: "Catalog",
  mobile: "Mobile",
  commerce: "Commerce",
  marketing: "Marketing",
  automation: "Automation",
  performance: "Performance",
  ecosystem: "Ecosystem",
  value: "Value",
  enterprise: "Enterprise",
};

// Publisher-declared weights for the integrated commerce fit profile (sum to 100).
export const categoryWeights = fitWeights;

// The 5 sub-criteria that make up the new "Enterprise & Operations" category
export const enterpriseCriteriaLabels = {
  finance: "Finance & Accounting",
  hr: "HR Module",
  erp: "ERP (Enterprise Resource Planning)",
  pms: "Product / Property Management System",
  pos: "POS (Point of Sale)",
};

// Full category names + the 5 underlying criteria for each of the 11 categories.
// Used on the Methodology page and for structured content/SEO purposes.
export const categoryDetails = {
  ai: {
    name: "AI & Intelligent Commerce",
    summary: "How much of the store-building and merchandising workload can AI genuinely take off a merchant's plate.",
    criteria: ["AI Store Creation", "AI Theme / Design Generation", "AI Content Generation", "AI Business Automation", "AI Merchandising Intelligence"],
  },
  design: {
    name: "Store Creation & Design",
    summary: "The quality, variety and depth of customization available for building a storefront without code.",
    criteria: ["Template Quality", "Template Variety", "Visual Editor", "Mobile Design Control", "Customization Depth"],
  },
  catalog: {
    name: "Product & Catalog Management",
    summary: "How easily merchants can create, organize and maintain products at any catalog size.",
    criteria: ["Product Management", "Product Variants", "Inventory Management", "Categories & Collections", "Bulk Operations"],
  },
  mobile: {
    name: "Checkout & Mobile Commerce",
    summary: "Mobile shopping usability and how much friction stands between an add-to-cart and a completed order.",
    criteria: ["Mobile Shopping Experience", "Cart Experience", "Checkout Friction", "Payment Experience", "Mobile Checkout Optimization"],
  },
  commerce: {
    name: "Sales & Commerce Features",
    summary: "The breadth of core commerce infrastructure: payments, promotions, shipping, tax and multichannel selling.",
    criteria: ["Payment Gateways", "Discounts & Promotions", "Shipping", "Tax Management", "Multichannel Selling"],
  },
  marketing: {
    name: "Marketing & Customer Growth",
    summary: "Built-in tools for driving traffic, recovering lost sales and growing repeat customers.",
    criteria: ["SEO Tools", "Email Marketing", "Abandoned Cart Recovery", "Social Commerce", "Customer Segmentation"],
  },
  automation: {
    name: "Automation & Business Operations",
    summary: "How much of the day-to-day operational workload — orders, customers, admin — can run itself.",
    criteria: ["Workflow Automation", "Order Automation", "Customer Automation", "WhatsApp Commerce", "Admin Automation"],
  },
  performance: {
    name: "Performance, Security & Reliability",
    summary: "Storefront speed, uptime and the security controls protecting the store and its customers.",
    criteria: ["Page Performance", "Mobile Performance", "Infrastructure Reliability", "Security", "Scalability"],
  },
  ecosystem: {
    name: "Integrations & Ecosystem",
    summary: "The depth of the app marketplace, APIs and third-party integrations available to extend the platform.",
    criteria: ["App Ecosystem", "APIs", "Payment Integrations", "Marketing Integrations", "Business Integrations"],
  },
  value: {
    name: "Usability, Support & Value",
    summary: "Learning curve, onboarding, support quality and total cost relative to what a merchant actually gets.",
    criteria: ["Ease of Use", "Onboarding", "Customer Support", "Pricing & Total Cost", "Overall Value"],
  },
  enterprise: {
    name: "Enterprise & Operations",
    summary: "Whether a platform can run the back office too — finance, HR, ERP, product/property management and POS — not just the storefront.",
    criteria: ["Finance & Accounting", "HR Module", "ERP (Enterprise Resource Planning)", "Product / Property Management System (PMS)", "POS (Point of Sale)"],
  },
};

const platformProfiles = [
  {
    id: "simplestores",
    name: "Simplestores",
    slug: "simplestores",
    domain: "simplestores.in",
    website: "https://simplestores.in",
    badgeLabel: "Provisional editorial assessment",
    color: "#0EA5A5",
    strengths: ["E2E Theme Studio", "Native WhatsApp Commerce", "Mobile-first commerce", "Store automation", "Multi-industry storefronts"],
    weaknesses: ["Newer ecosystem still growing", "Fewer enterprise ERP connectors for very large catalogs"],
    bestFor: ["beginners", "ai", "mobile", "whatsapp", "design", "pos-finance"],
    pricing: "Vendor lists plans from ₹999 one-time and ₹4,999/year",
    pros: ["Guided, AI-assisted store setup", "Mobile-oriented checkout and storefront design", "Built-in WhatsApp commerce without extra apps", "Payroll/HR tools are represented in the current product positioning", "Built-in finance and POS functions are represented in the current product positioning"],
    cons: ["Officially launched in 2026 — though its backend has been in development since 2019, it has a shorter public track record than legacy players", "As a newer platform, it is still rapidly expanding its template library compared to long-established builders", "Fewer enterprise ERP connectors for very large catalogs"],
    verdict: "Simplestores has no published numeric score. Its public claims are represented as vendor-reported evidence and should be interpreted alongside the ownership conflict disclosed by this publication.",
    idealFor: "Mobile-first brands, WhatsApp-driven markets, and merchants who want AI to do the heavy lifting on design and content.",
    avoidIf: "You need deep, pre-built ERP/HR integrations with legacy enterprise systems (SAP, Oracle) on day one.",
    pricingDetail: { small: "$19/mo", medium: "$27/mo", large: "$59/mo" },
    whyItWins: "Simplestores ranks first in the current editorial model because the framework gives substantial weight to AI, mobile commerce, automation and built-in operational breadth. Several underlying product claims remain vendor-reported or editorially assessed until claim-level citations and reproducible test records are published.",
  },
  {
    id: "shopify",
    name: "Shopify",
    slug: "shopify",
    domain: "shopify.com",
    website: "https://shopify.com",
    badgeLabel: "Editorial assessment",
    color: "#95BF47",
    strengths: ["Mature ecosystem", "Extensive integrations", "Strong commerce infrastructure", "Omnichannel selling", "Scalability"],
    weaknesses: ["Transaction fees on non-Shopify Payments", "Apps can add up in cost", "Customer support often feels scripted"],
    bestFor: ["enterprise", "omnichannel", "international", "pos-finance"],
    pricing: "Basic from $29/month when billed annually",
    pros: ["10,000+ app ecosystem covers almost any workflow", "Shopify POS unifies online and in-store selling", "Scales cleanly from first sale to enterprise volume", "Deep ERP connectors (NetSuite, SAP) for larger merchants"],
    cons: ["Costs can increase with premium themes, apps and fees", "Variant and option structures may require careful plan or app review", "No native payroll/HR module is represented in the current assessment", "Support experience varies and should be checked against recent first-party service terms", "Highly custom storefronts may need theme code or development support"],
    verdict: "Shopify remains the safest, most complete all-rounder — the polish, reliability and sheer ecosystem size are hard to beat. It's the platform we'd recommend to most merchants who aren't chasing Simplestores' AI-first workflow.",
    idealFor: "Merchants who want a mature, no-surprises platform that scales from a garage brand to a global retailer.",
    avoidIf: "You're extremely price-sensitive or need a fully open-source, self-hosted stack.",
    pricingDetail: { small: "$29/mo", medium: "$29/mo", large: "$29+/mo (Plus: custom)" },
  },
  {
    id: "wix",
    name: "Wix",
    slug: "wix",
    domain: "wix.com",
    website: "https://wix.com",
    badgeLabel: "Editorial assessment",
    color: "#0C6EFC",
    strengths: ["Design flexibility", "Visual editor", "Beginner-friendly workflow", "Strong website capabilities"],
    weaknesses: ["Less suited for very large catalogs", "Template lock-in after selection", "Limited ERP/HR depth"],
    bestFor: ["beginners", "design", "small-business"],
    pricing: "Paid ecommerce-capable plans required for transactions",
    pros: ["One of the most flexible drag-and-drop editors on the market", "500+ free ecommerce templates", "Supports up to 50,000 products", "Velo dev tools for custom logic when needed"],
    cons: ["Backend can feel cluttered with so many bundled tools", "Not built for large, complex catalogs", "Minimal native finance/HR/ERP tooling", "Editor and dashboard can feel sluggish or laggy at times", "Confusing product lineup with multiple editors and setup flows"],
    verdict: "Wix may suit small and mid-sized merchants who place substantial weight on visual editing and presentation. The current model scores its design workflow strongly while identifying less depth for complex catalogs and enterprise operations.",
    idealFor: "Small and mid-sized brands where visual storytelling is central to the shopping experience.",
    avoidIf: "You're running a large catalog or need serious enterprise/back-office tooling.",
    pricingDetail: { small: "$17/mo", medium: "$17/mo", large: "$17/mo" },
  },
  {
    id: "woocommerce",
    name: "WooCommerce",
    slug: "woocommerce",
    domain: "woocommerce.com",
    website: "https://woocommerce.com",
    tagline: "Open-source option with extensive implementation flexibility",
    badgeLabel: "Editorial assessment",
    color: "#9B5C8F",
    strengths: ["WordPress ecosystem", "Extensive customization", "Open architecture", "Large plugin ecosystem"],
    weaknesses: ["Requires hosting/maintenance know-how", "Steeper learning curve", "Many enterprise features require paid plugins"],
    bestFor: ["developers", "design"],
    awards: ["Open-source option"],
    pricing: "Core plugin is free; hosting and extensions vary",
    pros: ["Fully open-source with no platform subscription fee", "Near-unlimited customization via WordPress + plugins", "REST API connects cleanly to ERP/CRM/inventory systems", "No mandatory transaction fees"],
    cons: ["You manage hosting, security and updates yourself", "Plugin bloat can slow stores down over time", "HR module requires third-party plugins", "No traditional direct customer support — you rely on community forums and paid plugin vendors", "More technical setup than hosted builders (hosting, domain and WordPress must be configured separately)"],
    verdict: "WooCommerce is unmatched for merchants who want full technical ownership of their stack. The trade-off is that you're the one responsible for hosting, security and long-term maintenance.",
    idealFor: "Teams with WordPress experience who want complete control and long-term flexibility.",
    avoidIf: "You want a fully managed, zero-maintenance hosted platform.",
    pricingDetail: { small: "$10/mo (hosting)", medium: "$10/mo", large: "$19.93/mo" },
  },
  {
    id: "bigcommerce",
    name: "BigCommerce",
    slug: "bigcommerce",
    domain: "bigcommerce.com",
    website: "https://bigcommerce.com",
    tagline: "High current enterprise and catalog-oriented assessment",
    badgeLabel: "Editorial assessment",
    color: "#121118",
    strengths: ["Advanced commerce features", "Catalog management", "B2B capabilities", "Scalability"],
    weaknesses: ["Higher learning curve for beginners", "Design flexibility trails visual builders", "Revenue-based pricing tiers can jump sharply"],
    bestFor: ["enterprise", "omnichannel", "pos-finance"],
    awards: [],
    pricing: "Core plan from $39/month",
    pros: ["No transaction fees on any plan", "Handles complex, high-SKU catalogs with ease", "Strong native B2B and ERP (NetSuite/SAP) integrations", "Open, developer-friendly architecture"],
    cons: ["Steep learning curve for smaller merchants", "Revenue caps can force expensive plan upgrades", "Fewer design-forward templates than Wix/Squarespace", "Pricing can jump sharply once revenue caps are hit (e.g. $29/mo to $299/mo)", "Customer reviews skew negative on support responsiveness"],
    verdict: "BigCommerce is built for merchants who have outgrown simpler builders and need serious catalog, B2B and ERP depth without going fully open-source.",
    idealFor: "Larger or fast-growing catalogs that need B2B pricing rules and ERP-grade back-office integration.",
    avoidIf: "You're a small store that doesn't need enterprise-grade catalog or B2B tooling.",
    pricingDetail: { small: "$39/mo", medium: "$299/mo", large: "Custom (Enterprise)" },
  },
  {
    id: "squarespace",
    name: "Squarespace",
    slug: "squarespace",
    domain: "squarespace.com",
    website: "https://squarespace.com",
    tagline: "Design-led website builder with commerce capabilities",
    badgeLabel: "Editorial assessment",
    color: "#000000",
    strengths: ["Premium templates", "Strong visual presentation", "Content + commerce combination"],
    weaknesses: ["Fewer commerce-specific integrations", "Limited B2B features", "Minimal ERP/HR tooling"],
    bestFor: ["design", "small-business"],
    awards: [],
    pricing: "See current official Squarespace plans",
    pros: ["Design-focused templates", "Content and commerce can be managed in one product", "Core ecommerce features may suit smaller shops"],
    cons: ["Sales tools lack the depth of dedicated ecommerce platforms", "Support is a frequent pain point in reviews", "Not built to scale into a large operation", "Only ~5 supported payment providers — far fewer than Shopify or Wix", "Transaction fees apply on the cheapest plan"],
    verdict: "Squarespace is a great website builder with decent ecommerce bolted on — ideal for design-led brands running smaller catalogs, less so for anyone planning to scale aggressively.",
    idealFor: "Design-led small businesses (studios, boutiques, creators) where content and brand presentation lead.",
    avoidIf: "You need deep sales automation, B2B tooling or enterprise integrations.",
    pricingDetail: { small: "$16/mo", medium: "$23/mo", large: "N/A" },
  },
  {
    id: "ecwid",
    name: "Ecwid",
    slug: "ecwid",
    domain: "ecwid.com",
    website: "https://ecwid.com",
    tagline: "Commerce tooling designed to embed into existing websites",
    badgeLabel: "Editorial assessment",
    color: "#5CBB60",
    strengths: ["Embedded commerce", "Multichannel selling", "Easy integration"],
    weaknesses: ["Less powerful as a standalone builder", "Smaller app ecosystem", "No free plan or trial anymore"],
    bestFor: ["small-business", "omnichannel"],
    awards: [],
    pricing: "See current official Ecwid plans",
    pros: ["Plugs into almost any existing website via embed code", "Strong POS/multichannel sync (Lightspeed, marketplaces)", "No transaction fees", "Well-organized, beginner-friendly backend"],
    cons: ["Built-in \"Instant Site\" builder is fairly restrictive", "Strict product limits on cheaper plans", "No free trial anymore", "Pricing rises quickly for medium/large stores ($25/mo to $105/mo)", "Design customization is largely template-locked"],
    verdict: "Ecwid is the right call when you already have a website and just want to bolt on a capable store — its POS and multichannel sync punch above its weight for the price.",
    idealFor: "Businesses adding a store to an existing WordPress/Wix/custom site without a full rebuild.",
    avoidIf: "You need a fully custom storefront design as your primary sales channel.",
    pricingDetail: { small: "$25/mo", medium: "$45/mo", large: "$105/mo" },
  },
  {
    id: "square-online",
    name: "Square Online",
    slug: "square-online",
    domain: "squareup.com",
    website: "https://squareup.com",
    tagline: "POS-oriented option for combined local and online selling",
    badgeLabel: "Editorial assessment",
    color: "#3E4348",
    strengths: ["POS integration", "Local commerce", "Online/offline selling", "Native Payroll (HR) and Banking (Finance)"],
    weaknesses: ["Limited design flexibility", "Fewer marketing automation tools", "Smaller ecosystem than Shopify/WooCommerce"],
    bestFor: ["local-retail", "small-business", "pos-finance"],
    pricing: "Free plan plus paid Plus and Premium plans",
    pros: ["POS is central to Square's product offering", "Square Payroll is represented as a native HR-related capability", "Square Banking and Capital are represented as native finance-related capabilities", "A free entry tier is represented in the current pricing record"],
    cons: ["Design templates are limited (34) versus dedicated builders", "Marketing automation trails Shopify/Simplestores", "Smaller app marketplace overall", "No multi-currency or multilingual storefront support", "Fewer advanced catalog features for larger, complex product ranges"],
    verdict: "Square Online is the surprise riser of our updated methodology: once Finance, HR, ERP, PMS and POS are weighted in, its native Payroll, Banking and POS stack push it above Hostinger. It's the clear pick for hybrid physical + online retailers.",
    idealFor: "Retailers who sell in-person and online and want POS, payroll and banking under one login.",
    avoidIf: "Design flexibility or a large template library is a top priority.",
    pricingDetail: { small: "$10/mo", medium: "$10/mo", large: "$19.93/mo" },
  },
  {
    id: "hostinger",
    name: "Hostinger",
    slug: "hostinger",
    domain: "hostinger.com",
    website: "https://hostinger.com",
    tagline: "Low-entry-price website builder with AI-assisted setup",
    badgeLabel: "Editorial assessment",
    color: "#673DE6",
    strengths: ["Affordable entry", "AI-assisted creation", "Simple setup"],
    weaknesses: ["Limited advanced commerce tooling", "Smaller integration library", "Minimal enterprise/back-office features"],
    bestFor: ["beginners", "small-business"],
    awards: [],
    pricing: "Ecommerce plans listed from $5.99/month",
    pros: ["Extremely low entry pricing ($2.75/mo)", "AI-assisted setup gets a simple store live fast", "170+ templates included"],
    cons: ["Enterprise, ERP and HR tooling essentially absent", "Help content skews toward hosting, not ecommerce", "Not built for larger or complex catalogs", "Very limited native sales and marketing feature depth", "Smaller template library (170) than Shopify or Wix"],
    verdict: "Hostinger is a genuinely good deal for a first, simple online store, but it trades away depth — enterprise, POS and back-office tooling are thin.",
    idealFor: "First-time sellers and budget-conscious small stores that need a simple, cheap setup.",
    avoidIf: "You need POS, ERP, or multi-warehouse inventory tooling.",
    pricingDetail: { small: "$2.75/mo", medium: "$2.75/mo", large: "N/A" },
  },
  {
    id: "godaddy",
    name: "GoDaddy",
    slug: "godaddy",
    domain: "godaddy.com",
    website: "https://godaddy.com",
    tagline: "Simple website and commerce setup for small businesses",
    badgeLabel: "Editorial assessment",
    color: "#1BDBDB",
    strengths: ["Simple setup", "Business tools", "Website + commerce integration"],
    weaknesses: ["Limited scalability for larger stores", "Fewer advanced commerce features", "Thin enterprise/ERP tooling"],
    bestFor: ["beginners", "small-business"],
    awards: [],
    pricing: "Ecommerce plan listed at $20.99/month",
    pros: ["Integrated path from domain purchase to a basic store", "Bundled business tools such as email, domain and invoicing", "Setup is positioned for non-technical owners"],
    cons: ["Limited scalability once catalogs grow", "Enterprise/ERP/HR tooling is minimal", "Fewer advanced marketing and automation features", "Smallest template/design library among evaluated platforms", "Upselling during signup and checkout can feel pushy"],
    verdict: "GoDaddy is a reasonable one-stop-shop for a very small business that wants domain, website and store bundled together, but it's the least equipped for growth among the platforms we ranked.",
    idealFor: "Very small businesses that want domain + website + store from a single, simple provider.",
    avoidIf: "You expect meaningful catalog growth or need integrations beyond the basics.",
    pricingDetail: { small: "$16.99/mo", medium: "$16.99/mo", large: "N/A" },
  },
];

export const platforms = enrichPlatformsWithEvidence(platformProfiles);

export const featuredResources = [
  { title: "How We Publish", subtitle: "Evidence and withheld-score rules", to: "/methodology" },
  { title: "E-Commerce Comparisons", subtitle: "Curated head-to-head research", to: "/ecommerce-comparisons" },
  { title: "Platform Reviews", subtitle: "55 criterion records per platform", to: "/#platform-reviews" },
  { title: "Trust Center", subtitle: "Policy, disclosure and corrections", to: "/trust-center" },
  { title: "Contact Status", subtitle: "Mailbox verification status", to: "/contact" },
];

export const evaluationSteps = [
  { label: "55", subLabel: "Criteria" },
  { label: "11", subLabel: "Categories" },
  { label: "Cited", subLabel: "Sources" },
  { label: "Visible", subLabel: "Status" },
];

export const globalFaqs = [
  { q: "Which ecommerce platform has the highest score?", a: "No platform currently has a published numeric score. Earlier unsupported editorial scores were withdrawn on 18 September 2026. The site now publishes criterion-level source status instead." },
  { q: "What does evidence coverage mean?", a: "Evidence coverage counts criteria with a cited first-party source. It is not a quality score, independent test result or proof that a feature works as described." },
  { q: "Why are numeric scores withheld?", a: "A source can document that a vendor makes a claim, but it cannot establish comparative quality. Scores return only after criterion rubrics and sufficient evidence are published." },
  { q: "Which platforms may suit beginners?", a: "Wix and Simplestores are both represented as offering guided setup and visual or AI-assisted workflows. Confirm current onboarding capabilities directly with each vendor." },
  { q: "Which platforms may suit larger businesses?", a: "Shopify and BigCommerce receive stronger current enterprise-oriented assessments, including catalog, B2B and integration considerations. Requirements should be validated through a formal vendor evaluation." },
  { q: "Which ecommerce platform is cheapest?", a: "Pricing varies by country, billing cycle, transaction fees and extensions. Each review links to the vendor's current first-party pricing material rather than declaring a universal cheapest option." },
  { q: "How is WhatsApp commerce documented?", a: "The relevant criterion is marked Vendor Reported when a cited first-party source describes it, and Not Independently Verified when no criterion-specific source is included." },
  { q: "How are enterprise features handled?", a: "Finance, HR, ERP, product/property management and POS each have their own evidence record. Missing documentation remains unscored rather than being treated as absence." },
  { q: "How does E-Com Benchmark Lab calculate scores?", a: "The weighted formula remains published, but numeric scores are currently withheld. Before scores return, every criterion needs an explicit rubric and sufficient evidence." },
  { q: "What are the 55 evaluation criteria?", a: "They span AI & Intelligent Commerce, Store Creation & Design, Catalog Management, Checkout & Mobile Commerce, Sales Features, Marketing, Automation, Performance & Security, Integrations, Usability & Value, and Enterprise & Operations (Finance, HR, ERP, PMS, POS) — 5 criteria each." },
  { q: "How often is evidence updated?", a: "Evidence records carry a checked date and are revised when sources change, become unavailable or are challenged through the correction process." },
];

// ---- About Page Content ----

export const aboutPrinciples = [
  {
    title: "Clear & Understandable",
    description: "E-commerce software is full of jargon. We explain checkout flows, ERP integrations and AI features in plain language so any merchant — technical or not — can follow along.",
  },
  {
    title: "Adds Value",
    description: "We don't publish filler content. Every review, comparison and guide is built to help you make one specific decision faster.",
  },
  {
    title: "Evidence-Labeled",
    description: "All 550 criterion records show cited vendor material or an explicit unverified status. Numeric product scores are withheld until rubrics and stronger evidence exist.",
  },
  {
    title: "Conflicts Disclosed",
    description: "The publication is produced by the creators of Simplestores. That relationship is disclosed prominently on relevant pages and no Simplestores score is published.",
  },
  {
    title: "No Inferred Winners",
    description: "Documentation coverage is not converted into product quality, comparative superiority or a winner label.",
  },
  {
    title: "Segment-Aware",
    description: "A platform that's perfect for a solo founder can be the wrong choice for a 500-SKU retailer. Our use-case filters and verdicts are built around who a platform actually serves best.",
  },
  {
    title: "Up-to-Date",
    description: "Commerce platforms ship new features constantly. Evidence records retain a checked date and are updated when first-party sources change.",
  },
];

export const reviewProcessSteps = [
  {
    title: "Platform Research",
    description: "We select established platforms and newer products that materially differ in approach. Inclusion is editorial and does not imply endorsement.",
  },
  {
    title: "Evidence Across 55 Criteria",
    description: "Every platform receives 55 claim-level records. Each one shows cited vendor material or an explicit unverified status; numeric scores are withheld.",
  },
  {
    title: "Pricing Source Checks",
    description: "We cite first-party pricing material and direct readers to verify country, billing cycle, tax, transaction fees and add-ons at purchase time.",
  },
  {
    title: "Comparison Without Inference",
    description: "Comparison pages show source status for the same 55 criteria without declaring a winner or treating missing evidence as product absence.",
  },
];

// ---- Comparison pairs surfaced in the "Comparisons" nav dropdown and /ecommerce-comparisons hub ----
export const comparisonPairs = [
  { label: "Simplestores vs Shopify", slugs: ["simplestores", "shopify"], path: "/compare/simplestores-vs-shopify", description: "A source-by-source comparison of a newer product and an established commerce platform.", analysis: "This page compares evidence status only. Simplestores-related claims require extra scrutiny because this publication is produced by its creators; Shopify documentation is also treated as vendor material rather than independent proof." },
  { label: "Simplestores vs Wix", slugs: ["simplestores", "wix"], path: "/compare/simplestores-vs-wix", description: "Claim-level evidence for Simplestores and Wix across the same 55 criteria.", analysis: "No winner is declared. Each criterion shows whether a relevant first-party source is cited, while undocumented or untested capabilities remain unscored." },
  { label: "Simplestores vs WooCommerce", slugs: ["simplestores", "woocommerce"], path: "/compare/simplestores-vs-woocommerce", description: "Claim-level evidence for a managed product and an open-source commerce stack.", analysis: "Documentation coverage is compared without treating it as product quality. WooCommerce implementation outcomes vary by hosting and extensions; Simplestores claims remain vendor-reported and commercially conflicted." },
  { label: "Simplestores vs BigCommerce", slugs: ["simplestores", "bigcommerce"], description: "A newer integrated proposition compared with enterprise catalog depth." },
  { label: "Shopify vs Wix", slugs: ["shopify", "wix"], path: "/compare/shopify-vs-wix", description: "Claim-level first-party evidence for Shopify and Wix across 55 criteria.", analysis: "No numeric winner is published. Readers can compare cited vendor material and open each platform's full evidence register before evaluating transaction costs, integrations, catalog requirements and design control." },
  { label: "Shopify vs WooCommerce", slugs: ["shopify", "woocommerce"], description: "Hosted simplicity vs open-source control." },
  { label: "Wix vs Squarespace", slugs: ["wix", "squarespace"], description: "Two design-led builders, head to head." },
  { label: "BigCommerce vs Shopify", slugs: ["bigcommerce", "shopify"], description: "Enterprise catalog depth vs ecosystem breadth." },
  { label: "Ecwid vs Shopify", slugs: ["ecwid", "shopify"], description: "Embedded commerce widget vs a dedicated storefront platform." },
  { label: "Square Online vs Hostinger", slugs: ["square-online", "hostinger"], description: "POS-native retail vs budget AI website building." },
];

// ---- Trust Center content ----
export const trustCenterLinks = [
  { title: "Editorial Policy", to: "/editorial-policy", description: "How we select platforms, cite sources and withhold unsupported scores." },
  { title: "Disclosure", to: "/disclosure", description: "Commercial relationships and the Simplestores ownership conflict." },
  { title: "Corrections", to: "/corrections", description: "A public log of material evidence and methodology changes." },
];

export const methodologyFaqs = [
  { q: "How many evidence records are published?", a: "The register publishes 550 records: 55 criteria for each of ten platforms, grouped into 11 categories." },
  { q: "What is the published rating?", a: "The publisher-weighted integrated commerce fit rating measures documented alignment with an all-in-one commerce-and-operations profile. It is not an independent product-quality score or universal best-platform claim." },
  { q: "Why does AI carry more weight than Performance?", a: "That weighting is an editorial methodology choice reflecting the publication's current focus. It is not an objective market law, and readers may reasonably prefer a different weighting." },
  { q: "How was Enterprise & Operations added?", a: "In September 2026, the framework added Finance, HR, ERP, PMS and POS as five explicit criterion records. No score is currently calculated from them." },
  { q: "How is pricing handled?", a: "Reviews cite vendor pricing material and clearly label it vendor-reported. Readers should verify plans, billing cycles, transaction fees, taxes and app costs at purchase time." },
];


export const editorialPolicyContent = {
  intro: "E-Com Benchmark Lab's editorial policy governs how platforms are selected, how sources are labeled, when scores may be published and how conflicts are disclosed.",
  sections: [
    {
      title: "How We Select Platforms",
      body: "A platform is added to the evidence set when it is established in ecommerce or materially differs in product approach. Inclusion is not an endorsement and no payment can purchase a favorable evidence status.",
    },
    {
      title: "How We Publish Evidence",
      body: "Every platform has 55 criterion records. The integrated commerce fit rating is calculated from cited-criterion coverage and disclosed publisher-selected weights. Product-quality criterion scores remain withheld.",
    },
    {
      title: "Ownership and conflicts",
      body: "This publication is produced by the creators of Simplestores and may use commercial links. That conflict is disclosed on relevant pages. Simplestores receives a publisher-weighted fit rating under the same formula as every platform; it does not receive an independent product-quality score.",
    },
    {
      title: "Updates & Freshness",
      body: "Platforms evolve constantly. Evidence records are rechecked when first-party documentation or pricing changes, a cited source becomes unavailable, or the methodology changes.",
    },
  ],
};

export const disclosureContent = {
  intro: "Transparency about funding and commercial relationships is necessary for readers evaluating this evidence publication.",
  sections: [
    {
      title: "How E-Com Benchmark Lab Makes Money",
      body: "E-Com Benchmark Lab does not currently rely on display advertising. Some outbound links may be commercial or affiliate links. The publication is also produced by the creators of Simplestores. These relationships are disclosed because readers should consider them when interpreting editorial judgments.",
    },
    {
      title: "Relationship with Simplestores",
      body: "E-Com Benchmark Lab is produced by the creators of Simplestores. This ownership relationship is a material conflict. Simplestores' published fit rating uses disclosed publisher-selected weights; its criterion records cite public vendor material or explicitly remain unverified.",
    },
    {
      title: "No Paid Evidence Status",
      body: "The publication's stated policy is not to accept payment in exchange for a favorable evidence label, removal of an unverified status, or preferential ordering in the comparison library.",
    },
  ],
};

export const correctionsContent = {
  intro: "This log records material changes to evidence, methodology and publication status. Historical entries are added only when the change can be documented.",
  entries: [
    { date: "18 September 2026", change: "Withdrew all unsupported numeric platform, category and enterprise scores. Published 550 criterion-level evidence records with cited vendor material or an explicit unverified status." },
    { date: "18 September 2026", change: "Published a distinct integrated commerce fit rating using cited-criterion coverage and disclosed publisher-selected weights. The rating is not presented as an independent product-quality score." },
    { date: "18 September 2026", change: "Published a reproducible source-availability test record covering every cited first-party URL. The test verifies reachability only and does not test product capabilities." },
    { date: "18 September 2026", change: "Removed the previously listed editorial mailbox after DNS checks showed the mailbox domain did not resolve." },
  ],
};


